/**
 * Defensive Date helpers. The codebase has many legacy paths that pass
 * `TimelineDate` (string | number) into `new Date(value).toISOString()`. When
 * the value is empty, malformed, or NaN the throw is a `RangeError: Invalid
 * time value` that crashes whichever flow it's in.
 *
 * Every flow that needs to render or persist a date should funnel through one
 * of these helpers so a bad input degrades to a sensible fallback instead of
 * crashing the surrounding pipeline.
 */

const FALLBACK_DATE_ISO = () => new Date().toISOString()

/**
 * Convert any value to an ISO date-time string. Returns the optional fallback
 * (or "now") if the value can't be parsed.
 */
export const toSafeIso = (value: unknown, fallback?: string): string => {
  if (value instanceof Date) {
    if (!Number.isNaN(value.getTime())) return value.toISOString()
    return fallback ?? FALLBACK_DATE_ISO()
  }
  if (typeof value === 'string' || typeof value === 'number') {
    if (value === '' || value === null || value === undefined) {
      return fallback ?? FALLBACK_DATE_ISO()
    }
    const parsed = new Date(value)
    if (!Number.isNaN(parsed.getTime())) return parsed.toISOString()
  }
  return fallback ?? FALLBACK_DATE_ISO()
}

/**
 * Convert any value to a YYYY-MM-DD string suitable for `<input type="date">`.
 * Returns empty string if the value isn't a parseable date — let the input show
 * its placeholder rather than crash.
 */
export const toSafeDateInputValue = (value: unknown): string => {
  if (value instanceof Date) {
    if (!Number.isNaN(value.getTime())) return value.toISOString().slice(0, 10)
    return ''
  }
  if (typeof value !== 'string' && typeof value !== 'number') return ''
  if (value === '') return ''
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return ''
  return parsed.toISOString().slice(0, 10)
}

/**
 * Convert a value to YYYY-MM-DD using the supplied fallback if the value
 * doesn't parse. Use when an empty result is not acceptable (e.g. AI prompt
 * summaries that need *some* date string).
 */
export const toSafeDateOnly = (value: unknown, fallback: string): string => {
  if (value instanceof Date) {
    if (!Number.isNaN(value.getTime())) return value.toISOString().slice(0, 10)
    return fallback
  }
  if (typeof value !== 'string' && typeof value !== 'number') return fallback
  if (value === '') return fallback
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return fallback
  return parsed.toISOString().slice(0, 10)
}

/**
 * Returns true if the value parses to a valid Date.
 */
export const isValidDate = (value: unknown): boolean => {
  if (value instanceof Date) return !Number.isNaN(value.getTime())
  if (typeof value !== 'string' && typeof value !== 'number') return false
  if (value === '') return false
  return !Number.isNaN(new Date(value).getTime())
}
